import { useMutation } from "@apollo/client";
import { useRouter } from "next/router";
import { MdBuild } from "react-icons/md";
import { toast } from "sonner";

import { CloseIcon } from "@chakra-ui/icons";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { BuildStateEnum } from "@/__generated__/graphql";
import { ActionItem, Actions } from "@/components/Actions";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { operationErrorHandler } from "@/utils/operationErrorHandler";
import { buildPath } from "@/utils/pathGenerators";

const MBuildActions_Rebuild = graphql(`
  mutation MBuildActions_Rebuild($input: BuildsRebuildInput!) {
    buildsRebuild(input: $input) {
      build {
        id
        databaseId
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

const MBuildActions_Cancel = graphql(`
  mutation MBuildActions_Cancel($input: BuildsCancelInput!) {
    buildsCancel(input: $input) {
      build {
        ...BuildActions_Build
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

const BuildActions_Build = graphql(`
  fragment BuildActions_Build on Build {
    id
    databaseId
    state
  }
`);

interface Props {
  build: FragmentType<typeof BuildActions_Build>;
}

export function BuildActions(props: Props) {
  const build = getFragmentData(BuildActions_Build, props.build);
  const { workspaceId, applicationId, buildId } = useReadySearchParams();
  const [rebuild, { loading: rebuildLoading }] = useMutation(MBuildActions_Rebuild);
  const [cancel, { loading: cancelLoading }] = useMutation(MBuildActions_Cancel);

  const { push } = useRouter();

  const actions: ActionItem[] = [
    {
      type: "function",
      label: "Cancel",
      variant: "outline",
      icon: CloseIcon,
      isDisabled:
        build.state === BuildStateEnum.Errored ||
        build.state === BuildStateEnum.Done ||
        build.state === BuildStateEnum.Canceled,
      isLoading: cancelLoading,
      function: async () => {
        try {
          await cancel({ variables: { input: { id: buildId } } });

          toast.success("Build successfully cancelled");
        } catch (error) {
          operationErrorHandler(error);
        }
      },
    },
    {
      type: "function",
      label: "Rebuild",
      icon: MdBuild,
      isLoading: rebuildLoading,
      isDisabled: cancelLoading,
      function: async () => {
        try {
          const { data } = await rebuild({ variables: { input: { id: buildId } } });

          const newBuildId = data?.buildsRebuild?.build?.databaseId;
          if (!newBuildId) throw new Error("Could not create new build");

          toast.success("New build created successfully");
          void push(buildPath({ workspaceId, applicationId, buildId: newBuildId }));
        } catch (error) {
          operationErrorHandler(error);
        }
      },
    },
  ];

  return <Actions actions={actions} />;
}
